/**
 * ThreadsResurface — Sprint 2
 * When the traveller returns to a city where a past, sealed journey left loose
 * threads, this quietly surfaces them: "you left threads here." Picking one up
 * ties it to the current trip (sets resolvedTripId on the old thread).
 *
 * Sober and optional — renders nothing when there's nothing left hanging.
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { findUnresolvedThreadsForCity, resolveThread } from '../utils/sealManager';
import theme from '../styles/theme';

export default function ThreadsResurface({ city, activeTripId }) {
  const { t } = useTranslation('common');
  const [threads, setThreads] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!city) { setLoaded(true); return; }
    (async () => {
      const found = await findUnresolvedThreadsForCity(city, activeTripId).catch(() => []);
      if (!cancelled) { setThreads(found); setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [city, activeTripId]);

  const pickUp = async (th) => {
    if (activeTripId) await resolveThread(th.sourceTripId, th.id, activeTripId).catch(() => {});
    setThreads(prev => prev.filter(x => !(x.id === th.id && x.sourceTripId === th.sourceTripId)));
  };

  if (!loaded || threads.length === 0) return null;

  return (
    <div style={S.card}>
      <div style={S.title}>🧵 {t('seal.resurfaceTitle')}</div>
      <div style={S.desc}>{t('seal.resurfaceDesc', { city })}</div>
      <div style={S.list}>
        {threads.map(th => (
          <div key={`${th.sourceTripId}_${th.id}`} style={S.row}>
            <span style={S.text}>{th.text}</span>
            <button type="button" style={S.btn} onClick={() => pickUp(th)}>
              {t('seal.markResolved')}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const S = {
  card: {
    background: theme.card, border: `1px solid ${theme.bluePale}`, borderLeft: `3px solid ${theme.blue}`,
    borderRadius: 12, padding: '14px 16px', margin: '0 0 16px',
    boxShadow: '0 2px 10px rgba(45,90,110,0.08)',
  },
  title: { fontSize: 14, fontWeight: 700, color: theme.blueDeep, marginBottom: 4 },
  desc: { fontSize: 13, color: theme.brown, fontStyle: 'italic', marginBottom: 12 },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  row: { display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between' },
  text: { fontSize: 14, color: theme.brownDark, flex: 1, minWidth: 0 },
  btn: {
    flexShrink: 0, padding: '6px 12px', borderRadius: 10, border: 'none',
    background: theme.terracotta, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
  },
};
